/**
 * AxiomSupportSection.tsx
 *
 * "Contact Axiom Support" — open/view vendor support tickets about a
 * specific Axiom order. Distinct from our own end-user support_tickets/
 * Jira-sync system: this is us contacting AXIOM, not a clinic contacting us.
 * Rendered as the sole content of its own modal (opened from the orders
 * table's Axiom support action icon) — not inline under the order details.
 *
 * Axiom is the system of record for the actual thread, but we read/reply to
 * it through our own sidebar (getAxiomTicketDetail/postAxiomTicketMessage)
 * instead of embedding their widget — their /support-sessions endpoint mints
 * one session per ACCOUNT regardless of ticket_id (verified live 2026-08-07:
 * two sessions minted moments apart with different ticket_ids came back
 * byte-identical), so it can't actually show just one ticket's conversation.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/hooks/useToast';
import {
  listAxiomTickets,
  createAxiomTicket,
  getAxiomTicketDetail,
  postAxiomTicketMessage,
  type AxiomSupportTicket,
} from '@/services/integrations.service';
import { AutoGrowTextarea } from '@/components/ui/Textarea';

function describeApiError(e: any): string {
  return e?.response?.data?.error?.message ?? e?.message ?? 'Request failed';
}

function formatMessageTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function AxiomSupportSection({
  patientId,
  orderId,
  initialTicketId,
  onCreated,
}: {
  patientId: number;
  orderId: number;
  /** Open this ticket's thread (our axiomSupportTicketId) once the list loads. */
  initialTicketId?: number | null;
  /**
   * Fired after a ticket is successfully opened with Axiom. The section lives
   * inside a modal it does not own, so this is the only way it can ask to be
   * dismissed — and the caller may have a stacked order modal open behind it
   * (SCRUM-1464).
   */
  onCreated?: (ticket: AxiomSupportTicket) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<AxiomSupportTicket | null>(null);
  const [reply, setReply] = useState('');

  const queryKey = ['axiom-tickets', patientId, orderId];
  const { data: tickets, isLoading } = useQuery({
    queryKey,
    queryFn: () => listAxiomTickets(patientId, orderId),
  });

  // Arriving from "All tickets": go straight to the ticket that was clicked.
  const [initialApplied, setInitialApplied] = useState(false);
  useEffect(() => {
    if (initialApplied || !initialTicketId || !tickets) return;
    const match = tickets.find((t) => t.axiomSupportTicketId === initialTicketId);
    if (match) setSelectedTicket(match);
    setInitialApplied(true);
  }, [initialApplied, initialTicketId, tickets]);

  const detailQueryKey = ['axiom-ticket-detail', patientId, orderId, selectedTicket?.axiomSupportTicketId];
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => getAxiomTicketDetail(patientId, orderId, selectedTicket!.axiomSupportTicketId),
    enabled: !!selectedTicket,
  });
  // Opening a ticket clears its "new from Axiom" flag on the server, so the
  // red dots/badges on the order lists must refresh (SCRUM-1994).
  useEffect(() => {
    if (!detail) return;
    for (const key of [['account-axiom-orders'], ['account-axiom_vials-orders'], ['integration-orders', patientId]]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  }, [detail, qc, patientId]);

  const createMut = useMutation({
    mutationFn: () => createAxiomTicket(patientId, orderId, subject.trim(), description.trim()),
    onSuccess: (ticket) => {
      toast.success('Ticket opened with Axiom');
      setShowForm(false);
      setSubject('');
      setDescription('');
      qc.invalidateQueries({ queryKey });

      // When the caller wants the whole stack dismissed, do NOT open the new
      // ticket's detail view first — it would render for one frame and vanish.
      if (onCreated) {
        onCreated(ticket);

        return;
      }
      setSelectedTicket(ticket);
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const replyMut = useMutation({
    mutationFn: () => postAxiomTicketMessage(patientId, orderId, selectedTicket!.axiomSupportTicketId, reply.trim()),
    onSuccess: () => {
      setReply('');
      qc.invalidateQueries({ queryKey: detailQueryKey });
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Axiom support</span>
        {!showForm && (
          <Button variant="ghost" size="sm" onClick={() => setShowForm(true)}>
            <LifeBuoy className="h-3.5 w-3.5 mr-1" /> Contact Axiom
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1"><Spinner className="h-3.5 w-3.5" /> Loading…</div>
      ) : tickets && tickets.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {tickets.map((t) => (
            <li key={t.axiomSupportTicketId}>
              <button
                type="button"
                onClick={() => setSelectedTicket(t)}
                className="w-full flex items-center justify-between gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-left hover:bg-slate-50"
              >
                <span className="truncate text-slate-700">{t.subject}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <Badge variant={t.status === 'resolved' ? 'success' : 'info'}>{t.status}</Badge>
                  <LifeBuoy className="h-3 w-3 text-slate-400" aria-label="Open Axiom support" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        !showForm && <p className="mt-1 text-xs text-slate-400">No tickets opened for this order yet.</p>
      )}

      {showForm && (
        <div className="mt-2 space-y-2 rounded-md border border-slate-200 p-3">
          <input
            type="text"
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <AutoGrowTextarea
            placeholder="What's going on with this order?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending || !subject.trim() || !description.trim()}
            >
              {createMut.isPending ? 'Opening…' : 'Open ticket'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} disabled={createMut.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <Sheet open={!!selectedTicket} onOpenChange={(open) => { if (!open) setSelectedTicket(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
          <SheetHeader className="p-4 border-b border-slate-100">
            <SheetTitle className="text-sm flex items-center gap-2">
              {selectedTicket?.subject}
              {detail?.ticket && (
                <Badge variant={detail.ticket.status === 'resolved' ? 'success' : 'info'}>{detail.ticket.status}</Badge>
              )}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {detailLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-400"><Spinner className="h-4 w-4" /> Loading conversation…</div>
            ) : detail && detail.messages.length > 0 ? (
              detail.messages.map((m) => {
                const fromUs = m.author === 'partner';
                return (
                  <div key={m.id} className={`flex ${fromUs ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${fromUs ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <p className={`mt-1 text-[11px] ${fromUs ? 'text-blue-100' : 'text-slate-400'}`}>
                        {fromUs ? 'Us' : 'Axiom'} · {formatMessageTime(m.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-slate-400">No messages yet.</p>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-100 p-3 flex items-end gap-2">
            <textarea
              placeholder="Reply to Axiom…"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={2}
              className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
            <Button
              size="sm"
              onClick={() => replyMut.mutate()}
              disabled={replyMut.isPending || !reply.trim()}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
